import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import componentUsersContent from '@components/UsersTable/UsersTable.content.json';
import useTranslate from '@hooks/useTranslate';
import {
  UserBlockReason,
  userBlockOptions,
  userContentCleanOptions,
  userPermanentBlockReasonOptions,
} from '@constants/userBlockOptions';
import moment from 'moment';
import IUser from '@interfaces/IUser';

interface UserBlockMenuProps {
  user: IUser;
  onBlock: (
    user: IUser,
    daysToUnblock?: number,
    daysToClean?: number,
    reason?: UserBlockReason,
  ) => void;
}

const UserBlockMenu: FunctionComponent<UserBlockMenuProps> = ({ user, onBlock, children }) => {
  const usersContent = useTranslate(componentUsersContent);
  const now = useMemo(() => moment(), []);
  const [daysToUnblock, setDaysToUnblock] = useState<number>(0);
  const [displayCleanMenu, setDisplayCleanMenu] = useState<boolean>(false);
  const [displayBlockMenu, setDisplayBlockMenu] = useState<boolean>(false);

  const onBlockHandler = useCallback(
    (daysToUnblock?: number) => {
      if (daysToUnblock && daysToUnblock > 30) {
        setDisplayCleanMenu(true);
        setDaysToUnblock(daysToUnblock);
      } else {
        onBlock(user, daysToUnblock);
      }
    },
    [onBlock, user],
  );

  const onCleanHandler = useCallback(
    (daysToClean?: number) => {
      onBlock(user, daysToUnblock, daysToClean);
    },
    [daysToUnblock, onBlock, user],
  );

  const onPermanentBlockHandler = useCallback(
    (reason: UserBlockReason) => {
      onBlock(user, undefined, undefined, reason);
    },
    [onBlock, user],
  );

  const onVisibleChangeHandler = useCallback((value: boolean) => {
    setDisplayBlockMenu(value);
    if (!value) {
      setDisplayCleanMenu(value);
    }
  }, []);

  return (
    <Dropdown
      visible={displayCleanMenu || displayBlockMenu}
      onVisibleChange={onVisibleChangeHandler}
      overlay={
        displayCleanMenu ? (
          <Menu>
            <Menu.Item
              key='сleanMenuHeader'
              disabled
            >
              {usersContent['сleanMenuHeader']}
            </Menu.Item>
            {userContentCleanOptions.map((option) => {
              const [count, unit, code] = option;
              const blockDate = now.clone().add(count, unit);
              const daysToClean = blockDate.diff(now, 'days');
              return (
                <Menu.Item
                  key={code}
                  onClick={() => onCleanHandler(daysToClean)}
                >
                  {usersContent[code]}
                </Menu.Item>
              );
            })}
          </Menu>
        ) : (
          <Menu triggerSubMenuAction='hover'>
            <Menu.Item
              key='blockMenuHeader'
              disabled
            >
              {usersContent['blockMenuHeader']}
            </Menu.Item>
            {userPermanentBlockReasonOptions.map((reason) => (
              <Menu.Item
                key={reason}
                onClick={() => onPermanentBlockHandler(reason)}
              >
                {usersContent[reason]}
              </Menu.Item>
            ))}
            <Menu.SubMenu
              key='temporary'
              title={usersContent.temporary}
            >
              {userBlockOptions.map((option) => {
                const [count, unit, code] = option;
                const blockDate = now.clone().add(count, unit);
                const daysToUnblock = blockDate.diff(now, 'days');
                return (
                  <Menu.Item
                    key={code}
                    onClick={() => onBlockHandler(daysToUnblock)}
                  >
                    {usersContent[code]}
                  </Menu.Item>
                );
              })}
            </Menu.SubMenu>
          </Menu>
        )
      }
      trigger={['click']}
      placement='bottomRight'
    >
      {children}
    </Dropdown>
  );
};

export default UserBlockMenu;
